// Branded PDF generator for the Sample Bottleneck Scorecard.
// Uses jsPDF only (no server round-trip, no extra deps).
import { jsPDF } from "jspdf";

const COLOR_INK = [10, 10, 10];
const COLOR_ORANGE = [234, 88, 12];
const COLOR_MUTED = [82, 82, 91];
const COLOR_BG = [250, 250, 250];
const COLOR_BORDER = [228, 228, 231];

export const SAMPLE_ROWS = [
  { area: "Lead Capture", score: 62, grade: "C", note: "Website form + phone captured; after-hours coverage missing." },
  { area: "Response Time", score: 48, grade: "D", note: "Avg first reply > 6h. ~30% of inbound leads never replied to." },
  { area: "Sales Follow-Up", score: 55, grade: "C", note: "No structured cadence past 2 touches. Quotes not chased." },
  { area: "Ops & Admin", score: 51, grade: "D", note: "Heavy manual data re-entry between CRM, calendar and spreadsheets." },
  { area: "Visibility & Reporting", score: 40, grade: "D", note: "No weekly pulse. Issues surface only after customer complaints." },
];

export const SAMPLE_TOP3 = [
  {
    title: "Deploy an AI voice + SMS responder",
    body: "Answer every after-hours inbound with an AI receptionist. Qualify, schedule and log to one pipeline.",
    impact: "~20–30% more booked leads",
    effort: "Low",
  },
  {
    title: "Automate quote follow-up cadence",
    body: "Trigger a three-step follow-up sequence automatically once a quote is sent. Stop depending on memory.",
    impact: "+12% quote-to-close",
    effort: "Low–Med",
  },
  {
    title: "Consolidate reporting into a weekly digest",
    body: "One automated Monday email with the pulse: inbound, quoted, closed, slipping. No more spreadsheet Sunday.",
    impact: "Save ~6 hrs/week",
    effort: "Med",
  },
];

export function downloadSampleScorecardPDF() {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const H = 297;
  const M = 16; // margin

  let y = 0;

  // ===== Header band =====
  doc.setFillColor(...COLOR_INK);
  doc.rect(0, 0, W, 40, "F");

  // Logo mark (simple drawn 'F' mark so PDF is fully offline)
  doc.setFillColor(...COLOR_ORANGE);
  doc.circle(M + 6, 20, 5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("FYNIX SYSTEMS", M + 16, 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(234, 88, 12);
  doc.text("BOTTLENECK · FIXED", M + 16, 24);

  // Doc reference (right-aligned)
  doc.setTextColor(180, 180, 180);
  doc.setFontSize(7.5);
  doc.text("DOC · BTK-SCORE · V1.0", W - M, 18, { align: "right" });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7.5);
  const today = new Date().toISOString().slice(0, 10);
  doc.text(`ISSUED · ${today}`, W - M, 24, { align: "right" });
  doc.text("CLASSIFICATION · SAMPLE", W - M, 30, { align: "right" });

  y = 52;

  // ===== Title =====
  doc.setTextColor(...COLOR_ORANGE);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("AI BOTTLENECK ASSESSMENT", M, y);
  doc.setTextColor(...COLOR_INK);
  doc.setFontSize(24);
  y += 9;
  doc.text("Bottleneck Scorecard — Sample", M, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...COLOR_MUTED);
  y += 7;
  doc.text(
    "Illustrative example only. Your real scorecard is built from your actual workflow",
    M,
    y
  );
  y += 5;
  doc.text("during your AI Bottleneck Assessment.", M, y);
  y += 8;

  // ===== Overall score block =====
  doc.setDrawColor(...COLOR_INK);
  doc.setLineWidth(0.5);
  doc.rect(M, y, W - 2 * M, 26);
  doc.setFillColor(...COLOR_BG);
  doc.rect(M, y, W - 2 * M, 26, "F");
  doc.rect(M, y, W - 2 * M, 26);

  doc.setTextColor(...COLOR_MUTED);
  doc.setFontSize(7);
  doc.text("OVERALL SCORE", M + 4, y + 6);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(32);
  doc.setTextColor(...COLOR_ORANGE);
  doc.text("51", M + 4, y + 20);

  doc.setFontSize(11);
  doc.setTextColor(...COLOR_MUTED);
  doc.text("/ 100", M + 22, y + 20);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_INK);
  doc.text("GRADE  D", W - M - 4, y + 10, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_MUTED);
  doc.text("Opportunity-rich.", W - M - 4, y + 17, { align: "right" });
  doc.text("Top fixes are low-effort, high-impact.", W - M - 4, y + 22, {
    align: "right",
  });

  y += 32;

  // ===== Area breakdown =====
  sectionLabel(doc, "AREA BREAKDOWN", y);
  y += 7;

  SAMPLE_ROWS.forEach((r) => {
    if (y > H - 60) {
      doc.addPage();
      y = M;
    }
    // Name
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...COLOR_INK);
    doc.text(r.area, M, y);

    // Score (right)
    doc.setFontSize(14);
    doc.text(String(r.score), W - M, y, { align: "right" });

    // Grade
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_ORANGE);
    doc.text(`GRADE ${r.grade}`, W - M, y + 4, { align: "right" });

    // Note
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_MUTED);
    const noteLines = doc.splitTextToSize(r.note, W - 2 * M - 28);
    doc.text(noteLines, M, y + 5);

    // Bar
    const barY = y + 5 + noteLines.length * 4 + 2;
    doc.setFillColor(...COLOR_BORDER);
    doc.rect(M, barY, W - 2 * M, 1.6, "F");
    doc.setFillColor(...COLOR_ORANGE);
    doc.rect(M, barY, ((W - 2 * M) * r.score) / 100, 1.6, "F");

    y = barY + 8;

    // Divider
    doc.setDrawColor(...COLOR_BORDER);
    doc.setLineWidth(0.2);
    doc.line(M, y - 3, W - M, y - 3);
  });

  y += 4;

  // ===== Top 3 priorities =====
  if (y > H - 90) {
    doc.addPage();
    y = M;
  }
  sectionLabel(doc, "TOP 3 PRIORITIES", y);
  y += 7;

  SAMPLE_TOP3.forEach((t, i) => {
    if (y > H - 50) {
      doc.addPage();
      y = M;
    }
    // Card
    const cardH = 28;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...COLOR_INK);
    doc.setLineWidth(0.4);
    doc.rect(M, y, W - 2 * M, cardH, "FD");

    // Number
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...COLOR_ORANGE);
    doc.text(`0${i + 1}`, M + 4, y + 10);

    // Title
    doc.setFontSize(12);
    doc.setTextColor(...COLOR_INK);
    doc.text(t.title, M + 16, y + 8);

    // Body
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...COLOR_MUTED);
    const lines = doc.splitTextToSize(t.body, W - 2 * M - 24);
    doc.text(lines, M + 16, y + 13);

    // Impact + effort (right)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_INK);
    doc.text("IMPACT", W - M - 4, y + 15, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_ORANGE);
    doc.text(t.impact, W - M - 4, y + 19, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(...COLOR_INK);
    doc.text("EFFORT", W - M - 4, y + 23, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_MUTED);
    doc.text(t.effort, W - M - 4, y + 27, { align: "right" });

    y += cardH + 4;
  });

  // ===== Methodology =====
  if (y > H - 50) {
    doc.addPage();
    y = M;
  }
  y += 4;
  sectionLabel(doc, "METHODOLOGY", y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COLOR_MUTED);
  const method = doc.splitTextToSize(
    "The Fynix scorecard assesses five dimensions: Lead Capture, Response Time, Sales Follow-Up, Operations & Admin, and Visibility & Reporting. Each is scored 0–100 based on observed workflow gaps, tool coverage and response performance. The top three priorities are ranked by estimated business impact against implementation effort.",
    W - 2 * M
  );
  doc.text(method, M, y);
  y += method.length * 4.4;

  // ===== Footer band (drawn on every page) =====
  addFooter(doc);

  doc.save("Fynix_Sample_Bottleneck_Scorecard.pdf");
}

function sectionLabel(doc, text, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...COLOR_ORANGE);
  doc.text(text, 16, y);
  doc.setDrawColor(...COLOR_INK);
  doc.setLineWidth(0.5);
  doc.line(16, y + 1.5, 210 - 16, y + 1.5);
}

function addFooter(doc) {
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setDrawColor(228, 228, 231);
    doc.setLineWidth(0.2);
    doc.line(16, 285, 210 - 16, 285);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(82, 82, 91);
    doc.text("FYNIX SYSTEMS · BOTTLENECK · FIXED", 16, 290);
    doc.text(`PAGE ${i} / ${total}`, 210 - 16, 290, { align: "right" });
    doc.text("SAMPLE · NOT A CLIENT REPORT", 105, 290, { align: "center" });
  }
}
